/**
 * **背景渲染档位。**
 *
 * 评审：这个数现在承载了三种**完全不同的**画法，名字得跟上。
 *
 * | 档位 | 画法 | 这一档在干什么 |
 * | --- | --- | --- |
 * | `0` | **原样** | 全分辨率、不过纹理池那一圈（没有邻近采样、网格也不吸像素格）—— 就是像素化之前的样子 |
 * | `> 0` | **像素化** | 场渲进 `1/2^n` 屏幕分辨率的 FBO，邻近采样放大；**半调网格同时线性放大 `n` 倍**（不是 2^n） |
 * | `< 0` | **ASCII art** | 整个半调被 ASCII 取代，**不像素化**：每一个字符只采样一次标量场，整幅一起画出来。`|n|` 越大，单个字符越大 |
 *
 * ⚠️ 三种画法的差别不是"分辨率高低"，而是**媒介不同**（印刷网点 / 低分辨率印刷 /
 *    字符终端）。所以这里叫"渲染档位"而不是"倍率" —— 上一版叫
 *    `FIELD_PIXEL_EXPONENT`，那个名字只说得清中间那一档。
 *
 * ⚠️ 正档位那两条**不是同一个指数**：像素化是 `2^n`（每档四倍像素量），
 *    而半调网格是**线性**的 `n` 倍（评审：「半调网格也跟着线性放大，
 *    不和像素化一样 2^x 放大」）。写成同一个数的话，n=2 时网点会有 44 像素宽，
 *    整块背景变成几十个大色块。
 */

/**
 * **背景渲染档位。** 硬编码参数（界面里没有入口，改它要改代码 + 重新构建）。
 *
 * 默认 `0` = 原样。评审看过像素化那一版之后说「还是有点丑了」，而另外两种画法
 * 现在随时可以切：
 *
 * | 想要 | 设成 |
 * | --- | --- |
 * | 原来那张（全分辨率半调） | `0` ← 现在 |
 * | 复古像素风（1/2 分辨率 + 邻近采样 + 11px 网点） | `1` |
 * | 更粗的像素风（1/4 分辨率 + 22px 网点） | `2` |
 * | 字符终端（字符格 12 逻辑像素） | `-1` |
 * | 字符终端（一格 24 像素，读得清笔画） | `-2` |
 * | 字符终端（一格 36 像素，大字报） | `-3` |
 */
export const FIELD_RENDER_SCALE = 0;

/** ASCII 档位下**单个字符的基准边长**（逻辑像素）。`|scale|` 倍的线性放大 */
export const ASCII_CELL_BASE = 12;

/**
 * **`0` 档要不要画成"1-bit 硬边印刷品"。**
 *
 * | | 画法 |
 * | --- | --- |
 * | `true` | 二值化（屏幕上只有黑白两级）+ 采样点吸像素网格 + 情绪亮度走覆盖率 |
 * | `false`（现在） | **加像素化之前的样子**（`43089c0` 那一版）：柔边半调（`smoothstep` 抗锯齿）、不吸像素格、情绪亮度乘颜色 |
 *
 * ⚠️ **只在 `FIELD_RENDER_SCALE = 0` 时有意义**（见 `halftoneBinaryOf`）：
 *    像素化档（`> 0`）的硬边方块就是那一档本身的样子；ASCII 档（`< 0`）有它
 *    自己的二值化（那是字形）。
 *
 * ⚠️ 评审的原话：「目前 `FIELD_RENDER_SCALE` 为 0 时的效果仍是像素化且二值化的
 *    ……新增一个参数，使得像素化为否且 SCALE 为 0 时的行为和之前一致」。
 */
export const FIELD_HALFTONE_BINARY = false;

/** 档位的合法范围。再往外不是"更糊"就是"看不见"，没有意义 */
export const RENDER_SCALE_MIN = -8;
export const RENDER_SCALE_MAX = 4;

/** 三种画法 */
export type RenderMode = 'plain' | 'pixel' | 'ascii';

/** 把档位夹成合法值：取整、限幅，`NaN` 当 0（原样） */
export function normalizeRenderScale(value: number): number {
  if (!Number.isFinite(value)) return 0;
  const rounded = Math.trunc(value);
  return Math.max(RENDER_SCALE_MIN, Math.min(RENDER_SCALE_MAX, rounded));
}

export function renderMode(scale: number): RenderMode {
  const value = normalizeRenderScale(scale);
  if (value > 0) return 'pixel';
  if (value < 0) return 'ascii';
  return 'plain';
}

/**
 * **降分辨率用的是 `2^n`**（只有像素化档位才有意义，别的档位一律 0 = 不降）。
 *
 * 叫 `…Exponent` 而不是 `…Scale` 就是为了让"这是指数"这件事写在名字上 ——
 * 正档位那两条一个是 2^n、一个是 n，混起来最容易错。
 */
export function pixelExponentOf(scale: number): number {
  const value = normalizeRenderScale(scale);
  return value > 0 ? value : 0;
}

/**
 * **半调网格线性放大几倍**（只有像素化档位才放大）。
 *
 * 非像素化档位是 1（网格保持设计尺寸 11 逻辑像素）。
 */
export function halftoneGridScale(scale: number): number {
  const value = normalizeRenderScale(scale);
  return value > 0 ? value : 1;
}

/**
 * **ASCII 字符格的边长**（逻辑像素）。不是 ASCII 档位时是 0。
 *
 * `|n|` 越大格子越大（评审：「负值越高，单个 ASCII 字符尺寸越大」）——
 * 线性，不是 `2^|n|`。
 */
/**
 * **这一档要不要硬边二值化。**
 *
 * - `> 0`（像素化）：**是** —— 硬邦邦的方块就是那一档的定义
 * - `< 0`（ASCII）：**不是**（半调那一级根本不跑，字形自己有二值化）
 * - `= 0`：看 `FIELD_HALFTONE_BINARY`
 */
export function halftoneBinaryOf(scale: number): boolean {
  const value = normalizeRenderScale(scale);
  if (value > 0) return true;
  if (value < 0) return false;
  return FIELD_HALFTONE_BINARY;
}

export function asciiCellSize(scale: number): number {
  const value = normalizeRenderScale(scale);
  return value < 0 ? ASCII_CELL_BASE * -value : 0;
}

/**
 * ASCII 档位下屏幕上有多少列 / 多少行字符。
 *
 * ⚠️ 它同时是**场的采样数**：每个字符采样一次标量场，所以场就渲在
 *    `cols × rows` 那么小的一张 FBO 上 —— 每个字符刚好一个纹素。
 */
export function asciiGrid(
  scale: number,
  width: number,
  height: number,
): { readonly cols: number; readonly rows: number } {
  const cell = asciiCellSize(scale);
  if (cell <= 0) return { cols: 0, rows: 0 };
  return {
    cols: Math.max(1, Math.ceil(width / cell)),
    rows: Math.max(1, Math.ceil(height / cell)),
  };
}
